<script>
import Spinner from 'shared/components/Spinner.vue';
import { useAlert } from 'dashboard/composables';
import { mapGetters } from 'vuex';
import { useAgentsList } from 'dashboard/composables/useAgentsList';

import MultiselectDropdownItems from 'shared/components/ui/MultiselectDropdownItems.vue';

export default {
  components: {
    Spinner,
    MultiselectDropdownItems,
  },
  props: {
    conversationId: {
      type: [Number, String],
      required: true,
    },
  },
  setup() {
    const { agentsList } = useAgentsList(false);
    return {
      agentsList,
    };
  },
  data() {
    return {
      selectedWatchers: [],
      showDropDown: false,
    };
  },
  computed: {
    ...mapGetters({
      watchersUiFlas: 'conversationWatchers/getUIFlags',
      currentUser: 'getCurrentUser',
    }),
    watchersFromStore() {
      return this.$store.getters['conversationWatchers/getByConversationId'](
        this.conversationId
      );
    },
    watchersList: {
      get() {
        return this.selectedWatchers;
      },
      set(participants) {
        this.selectedWatchers = [...participants];
        const userIds = participants.map(el => el.id);
        this.updateParticipant(userIds);
      },
    },
    isUserWatching() {
      return this.selectedWatchers.some(
        watcher => watcher.id === this.currentUser.id
      );
    },
  },
  watch: {
    conversationId() {
      this.fetchParticipants();
    },
    watchersFromStore(participants = []) {
      this.selectedWatchers = [...participants];
    },
  },
  mounted() {
    this.fetchParticipants();
    this.$store.dispatch('agents/get');
  },
  methods: {
    fetchParticipants() {
      const conversationId = this.conversationId;
      this.$store.dispatch('conversationWatchers/show', { conversationId });
    },
    async updateParticipant(userIds) {
      const conversationId = this.conversationId;
      let alertMessage = this.$t(
        'CONVERSATION_PARTICIPANTS.API.SUCCESS_MESSAGE'
      );

      try {
        await this.$store.dispatch('conversationWatchers/update', {
          conversationId,
          userIds,
        });
        // Auto-assign first participant as primary agent
        if (userIds.length > 0) {
          await this.$store.dispatch('assignAgent', {
            conversationId,
            agentId: userIds[0],
          });
        } else {
          await this.$store.dispatch('assignAgent', {
            conversationId,
            agentId: 0,
          });
        }
      } catch (error) {
        alertMessage =
          error?.message ||
          this.$t('CONVERSATION_PARTICIPANTS.API.ERROR_MESSAGE');
      } finally {
        useAlert(alertMessage);
      }
      this.fetchParticipants();
    },
    onOpenDropdown() {
      this.showDropDown = true;
    },
    onCloseDropdown() {
      this.showDropDown = false;
    },
    onClickItem(agent) {
      // SolarZ: Somente 1 agente responsável por conversa.
      // Clicar em outro agente substitui o anterior; clicar no mesmo remove.
      const isSelected = this.watchersList.some(p => p.id === agent.id);
      this.watchersList = isSelected ? [] : [agent];
      this.showDropDown = false;
    },
    onRemoveAgent(agent) {
      const updatedList = this.watchersList.filter(
        participant => participant.id !== agent.id
      );
      this.watchersList = [...updatedList];
    },
    onSelfAssign() {
      // Só atribui a si mesmo, substituindo qualquer outro
      this.watchersList = [this.currentUser];
    },
  },
};
</script>

<template>
  <div class="border-t border-n-weak">
    <div
      v-on-clickaway="onCloseDropdown"
      class="relative px-4 py-3"
    >
      <div class="flex items-center justify-between mb-2">
        <h4 class="text-sm font-medium text-n-slate-12 m-0">
          Agente responsável
        </h4>
        <button
          v-if="!isUserWatching"
          class="text-xs text-n-blue-text cursor-pointer border-0 bg-transparent p-0 hover:underline"
          @click="onSelfAssign"
        >
          Assumir conversa
        </button>
        <span v-else class="text-xs text-n-slate-10">Você está atribuído</span>
      </div>

      <!-- Chip multi-select container -->
      <div
        class="flex flex-wrap items-center gap-1.5 px-2 py-1.5 rounded-lg border border-n-weak min-h-[36px] cursor-pointer"
        @click="onOpenDropdown"
      >
        <Spinner v-if="watchersUiFlas.isFetching" size="tiny" />
        <template v-else>
          <!-- Agent chips -->
          <div
            v-for="watcher in selectedWatchers"
            :key="watcher.id"
            class="flex items-center gap-1 pl-0.5 pr-1.5 py-0.5 rounded-full bg-n-slate-2"
          >
            <div
              class="w-5 h-5 rounded-full overflow-hidden bg-n-slate-3 flex items-center justify-center shrink-0"
            >
              <img
                v-if="watcher.thumbnail"
                :src="watcher.thumbnail"
                class="w-full h-full object-cover"
              />
              <span v-else class="text-[10px] text-n-slate-11 font-medium">
                {{ watcher.name?.charAt(0)?.toUpperCase() }}
              </span>
            </div>
            <span class="text-sm text-n-slate-12 whitespace-nowrap">
              {{ watcher.name }}
            </span>
            <button
              class="text-n-slate-10 hover:text-n-slate-12 cursor-pointer border-0 bg-transparent p-0 flex items-center"
              @click.stop="onRemoveAgent(watcher)"
            >
              <fluent-icon icon="dismiss" size="12" />
            </button>
          </div>

          <!-- Placeholder when empty -->
          <span v-if="!selectedWatchers.length" class="text-sm text-n-slate-10">
            Selecionar responsável
          </span>
        </template>

        <!-- Dropdown chevron -->
        <fluent-icon
          icon="chevron-down"
          size="14"
          class="ml-auto text-n-slate-10 shrink-0"
        />
      </div>

      <!-- Agent picker dropdown -->
      <div
        v-if="showDropDown"
        class="absolute left-0 right-0 mt-1 mx-4 border rounded-lg bg-n-alpha-3 backdrop-blur-[100px] shadow-lg border-n-strong p-2 z-[9999]"
      >
        <div class="flex items-center justify-between mb-1">
          <h4 class="m-0 text-sm text-n-slate-11">Agente responsável</h4>
          <button
            class="text-n-slate-10 hover:text-n-slate-12 cursor-pointer border-0 bg-transparent p-0 flex items-center"
            @click.stop="onCloseDropdown"
          >
            <fluent-icon icon="dismiss" size="14" />
          </button>
        </div>
        <MultiselectDropdownItems
          :options="agentsList"
          :selected-items="selectedWatchers"
          has-thumbnail
          @select="onClickItem"
        />
      </div>
    </div>
  </div>
</template>
